import React from "react";
import { observer } from "mobx-react";
import { TenantLlmConfigUpdateStore } from "../../store";
import { TenantLlmConfigUpdateForm } from "./TenantLlmConfigUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";

export type TenantLlmConfigUpdateProps = {
  tenantLlmConfigUpdateStore: TenantLlmConfigUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantLlmConfigUpdate: React.FC<TenantLlmConfigUpdateProps> =
  observer(
    ({ tenantLlmConfigUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={tenantLlmConfigUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={tenantLlmConfigUpdateStore.savedButOutdated}
            onReload={() => tenantLlmConfigUpdateStore.reloadTenantLlmConfig()}
            reloadDisabled={tenantLlmConfigUpdateStore.isProcessing}
          />
          <TenantLlmConfigUpdateForm
            tenantLlmConfigUpdateStore={tenantLlmConfigUpdateStore}
            providerError={tenantLlmConfigUpdateStore.errors.provider?.message}
            onProviderChange={(value) => {
              tenantLlmConfigUpdateStore.setProviderValue(value);
            }}
            providerSelectedValue={
              tenantLlmConfigUpdateStore.entityValues.showProvider
            }
            onProviderSelected={(value) => {
              tenantLlmConfigUpdateStore.setSelectedProvider(value);
            }}
            providerOptions={tenantLlmConfigUpdateStore.getProviderOptions()}
            modelError={tenantLlmConfigUpdateStore.errors.model?.message}
            onModelChange={(value: string) => {
              tenantLlmConfigUpdateStore.setModelValue(value);
            }}
            model={tenantLlmConfigUpdateStore.entityValues.model}
            apiKeyError={tenantLlmConfigUpdateStore.errors.apiKey?.message}
            onApiKeyChange={(value: string) => {
              tenantLlmConfigUpdateStore.setApiKeyValue(value);
            }}
            apiKey={tenantLlmConfigUpdateStore.entityValues.apiKey}
            baseUrlError={tenantLlmConfigUpdateStore.errors.baseUrl?.message}
            onBaseUrlChange={(value: string) => {
              tenantLlmConfigUpdateStore.setBaseUrlValue(value);
            }}
            baseUrl={tenantLlmConfigUpdateStore.entityValues.baseUrl}
            enabledError={tenantLlmConfigUpdateStore.errors.enabled?.message}
            onEnabledChange={(value: boolean) => {
              tenantLlmConfigUpdateStore.setEnabledValue(value);
            }}
            enabled={tenantLlmConfigUpdateStore.entityValues.enabled}
            priorityError={tenantLlmConfigUpdateStore.errors.priority?.message}
            onPriorityChange={(value: number) => {
              tenantLlmConfigUpdateStore.setPriorityValue(value);
            }}
            priority={tenantLlmConfigUpdateStore.entityValues.priority}
            maxTokensError={
              tenantLlmConfigUpdateStore.errors.maxTokens?.message
            }
            onMaxTokensChange={(value: number | undefined) => {
              tenantLlmConfigUpdateStore.setMaxTokensValue(value);
            }}
            maxTokens={tenantLlmConfigUpdateStore.entityValues.maxTokens}
            temperatureError={
              tenantLlmConfigUpdateStore.errors.temperature?.message
            }
            onTemperatureChange={(value: number | undefined) => {
              tenantLlmConfigUpdateStore.setTemperatureValue(value);
            }}
            temperature={tenantLlmConfigUpdateStore.entityValues.temperature}
            debugFullModeError={
              tenantLlmConfigUpdateStore.errors.debugFullMode?.message
            }
            onDebugFullModeChange={(value: boolean) => {
              tenantLlmConfigUpdateStore.setDebugFullModeValue(value);
            }}
            debugFullMode={
              tenantLlmConfigUpdateStore.entityValues.debugFullMode
            }
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={tenantLlmConfigUpdateStore.saveDisabled}
            onSave={() => tenantLlmConfigUpdateStore.saveTenantLlmConfig()}
            onCancel={() => tenantLlmConfigUpdateStore.cancelTenantLlmConfig()}
          />
          <ErrorDialog
            open={tenantLlmConfigUpdateStore.errorDialogOpened}
            title={tenantLlmConfigUpdateStore.errorTitle}
            description={tenantLlmConfigUpdateStore.errorDescription}
            onClose={() => tenantLlmConfigUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={tenantLlmConfigUpdateStore.confirmDialogOpened}
            title={tenantLlmConfigUpdateStore.confirmDialogTitle}
            description={tenantLlmConfigUpdateStore.confirmDialogDescription}
            onConfirm={() =>
              tenantLlmConfigUpdateStore.executeConfirmedAction()
            }
            onCancel={() => tenantLlmConfigUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
